// Decision helpers that are not tied to React, so the harness can exercise
// them:
//   1. resolveDecisionBatch / skippedNote — what a bulk approve or deny should
//      actually write at the moment the reviewer confirms it.
//   2. loadDenialDecisionsSince — the denial/archive rows behind the
//      dashboard's "Why invoices are denied" panel.
//   3. importControlReleaseSentence — the Import Control release toast, which
//      names where the invoice went.
import { InvoiceStatus } from './constants'
import { sentenceList } from './format'

// ---- What a bulk decision should write at confirm ---------------------------
// A bulk approve/deny snapshots the checked ids when the dialog opens, and the
// queue keeps reloading behind it: by the time the reviewer confirms, a row may
// have left the queue, been put on hold, or fallen out of their authority. The
// broker rejects such a decision — after a 32-second fence wait and a rejected
// audit row — so the screens re-resolve the batch here at confirm instead.
interface DecisionBatch<T> {
  /** Rows still present and still actionable — the ones to write. */
  eligible: T[]
  /** Ids no longer in the queue (already handled elsewhere). */
  gone: string[]
  /** Ids still queued but now on hold or no longer decidable by this user. */
  blocked: string[]
}

export function resolveDecisionBatch<T>(
  ids: readonly string[],
  lookup: (id: string) => T | undefined,
  actionable: (row: T) => boolean,
): DecisionBatch<T> {
  const eligible: T[] = []
  const gone: string[] = []
  const blocked: string[] = []
  for (const id of ids) {
    const row = lookup(id)
    if (!row) gone.push(id)
    else if (!actionable(row)) blocked.push(id)
    else eligible.push(row)
  }
  return { eligible, gone, blocked }
}

/** One sentence naming what was dropped from a batch, or undefined if none was. */
export function skippedNote(batch: DecisionBatch<unknown>): string | undefined {
  const parts: string[] = []
  if (batch.gone.length) parts.push(`${batch.gone.length} already handled elsewhere`)
  if (batch.blocked.length) parts.push(`${batch.blocked.length} on hold or not yours to decide`)
  return parts.length ? `Some invoices were skipped: ${sentenceList(parts)}.` : undefined
}

/** Export Control's note on held invoices left out of an export (owner,
    2026-09-30, D-247), in the words Closing Confirmation's Confirm Import uses
    for the invoices it skips. Undefined when none were held. */
export function heldNote(count: number): string | undefined {
  if (count <= 0) return undefined
  return count === 1
    ? 'One invoice on hold was left out of the export. It stays here until the hold is released.'
    : `${count} invoices on hold were left out of the export. They stay here until their holds are released.`
}

/** The single Deny dialog's body, at the three tiers and Manual Entry — one
    constant, so the two screens cannot drift. It says nothing is final: a
    denied invoice can be reinstated from Denial Review (D-158, QA C2-01). And
    it says "any reason you give", not "this comment": the Reason is optional
    unless the Category is Other, so there may be none (QA C2-02). Two
    sentences, carried as-is from the initial port (2026-08-04); no owner
    ruling set its wording or its length, and the length is left for the
    owner (corrected after review, 2026-09-25). */
export const DENY_BODY =
  'Denied invoices route to Denial Review. Any reason you give is recorded on the invoice and visible in the decision history.'

/** BULK DENY'S BODY, singular for one invoice (QA 2026-10-04, C2-03): with one
    selected it read "send the selected invoice … where each can be archived
    … recorded on every selected invoice". */
export function bulkDenyBody(n: number): string {
  return n === 1
    ? 'Denying will immediately send the selected invoice to Denial Review, where it can be archived or reinstated. Any reason you give is recorded on the invoice and visible in the decision history.'
    : `Denying will immediately send the ${n} selected invoices to Denial Review, where each can be archived or reinstated. Any reason you give is recorded on every selected invoice and visible in the decision history.`
}

/** BULK APPROVE'S BODY, by the same rule as `bulkDenyBody`. */
export function bulkApproveBody(n: number): string {
  return n === 1
    ? 'Approving will immediately move the selected invoice to the next step. You cannot undo this action.'
    : `Approving will immediately move the ${n} selected invoices to the next step. You cannot undo this action.`
}

/** The Reinstate dialog's body, naming where the invoice goes (D-324). It
    named the step that denied the invoice until D-312, and Import Control
    from D-312 (worded by the owner, D-316) until D-324. */
export const REINSTATE_BODY =
  'The invoice will restart the approval process at its first qualifying approval step, including your Reason/Comments.'

// ---- Denial + archive decisions (dashboard panel) ---------------------------

/**
 * THE RELEASE TOAST AT IMPORT CONTROL SAYS WHERE THE INVOICE WENT (QA
 * 2026-09-30, C1-04 — `docs/decisions.md` D-237). A release goes to the first
 * step its required approvals name, staffed or not, else to Export Control when
 * none is required (D-336; D-218; Manual Entry too, until that step was dropped
 * — D-311). The toast used to say "released to its company's approvers" on
 * every route, including one that went straight to Export Control.
 *
 * `releasedTo` is the status the server answered with (`released_to` on the
 * editor's save, `new_status` on a decision), one per released invoice. A
 * missing status keeps the approvers sentence, the one the tier route has
 * always had.
 */
export function importControlReleaseSentence(releasedTo: readonly (string | null | undefined)[]): string {
  const count = releasedTo.length
  const where = (status: string | null | undefined): 'export' | 'tiers' =>
    status === InvoiceStatus.PendingExportControl ? 'export' : 'tiers'
  const tally = { tiers: 0, export: 0 }
  for (const status of releasedTo) tally[where(status)]++
  const subject = count === 1 ? 'The invoice was' : `${count} invoices were`
  const approvers = (n: number) => (n === 1 ? "its company's approvers" : "their companies' approvers")
  if (tally.export === count) return `${subject} approved and released to Export Control.`
  if (tally.tiers === count) return `${subject} approved and released to ${approvers(count)}.`
  const parts = [
    ...(tally.tiers > 0 ? [`${tally.tiers} to ${approvers(tally.tiers)}`] : []),
    ...(tally.export > 0 ? [`${tally.export} to Export Control`] : []),
  ]
  return `${subject} approved and released: ${sentenceList(parts)}.`
}
